import './SupportStatusToggle.scss';
import { Box, Button, Typography } from '@mui/material';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const SupportStatusToggle = ({ statusType, setStatus, data, label }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const liveRegionRef = useRef<HTMLDivElement>(null);

  const navigateToStatus = (status) => {
    let languagePrefix: string;

    switch (i18n.language) {
      case 'fr':
        languagePrefix = 'fr';
        break;
      default:
        languagePrefix = 'en';
        break;
    }
    let statusPath;
    if (status === 'Production') statusPath = 'PROD';
    else if (status === 'Gold') statusPath = 'GOLD';
    else if (status === 'BAT') statusPath = 'BAT';
    navigate(`/${languagePrefix}/support/statusMonitoring/${statusPath}`);
    setStatus(status);
  };

  const announceStatus = (status, label) => {
    if (liveRegionRef.current) {
      liveRegionRef.current.textContent = `${status} ${label}`;
    }
  };

  return (
    <Box className="toggleContainer">
      <div
        ref={liveRegionRef}
        aria-live="polite"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          margin: '-1px',
          border: '0',
          padding: '0',
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)'
        }}
      />
      <Box>
        <Typography component="h1" className="statusHeaderText">
          {data.title}
        </Typography>
        <Typography className="statusSubText">{data.description}</Typography>
      </Box>
      <fieldset
        aria-labelledby="toggle-group-legend"
        style={{ border: 'none', padding: 0, margin: 0 }}
      >
        <legend
          id="toggle-group-legend"
          style={{ color: 'transparent', height: 0, padding: 0, margin: 0 }}
        >
          {data.filterTopic}
        </legend>
        <Box
          className="toggle-group"
          role="group"
          sx={{ width: { xs: '100%', '500px': 'auto' } }}
        >
          <Box
            className="toggle"
            sx={{
              transform: {
                xs:
                  statusType === 'Production'
                    ? 'translateX(0%)'
                    : statusType === 'Gold'
                    ? 'translateX(100%)'
                    : 'translateX(200%)',
                '500px':
                  statusType === 'Production'
                    ? 'translateX(0px)'
                    : statusType === 'Gold'
                    ? 'translateX(100px)'
                    : 'translateX(200px)'
              }
            }}
          />
          <Button
            variant="text"
            sx={{
              color:
                statusType === 'Production' ? '#ffffff !important' : 'black',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '12px' ,
              textDecoration: 'none !important',
              '&:hover': {
                textDecoration: 'none'
              },
              '@media(max-width: 600px)': {
                fontSize: '11px !important'
              }
            }}
            aria-label="view Production status"
            aria-pressed={statusType === 'Production'}
            onClick={() => {
              setStatus('Production');
              navigateToStatus('Production');
              announceStatus('Production', label);
            }}
          >
            {data.toggle[0]}
          </Button>
          <Button
            variant="text"
            sx={{
              color: statusType === 'Gold' ? '#ffffff !important' : 'black',
              textTransform: 'none',
              fontWeight: 400,
              fontSize:'12px',
              textDecoration: 'none !important',
              '&:hover': {
                textDecoration: 'none'
              },
              '@media(max-width: 600px)': {
                fontSize: '11px !important'
              }
            }}
            aria-label="View UAT status"
            aria-pressed={statusType === 'Gold'}
            onClick={() => {
              setStatus('Gold');
              navigateToStatus('Gold');
              announceStatus('UAT', label);
            }}
          >
            {data.toggle[1]}
          </Button>
          <Button
            variant="text"
            sx={{
              color: statusType === 'BAT' ? '#ffffff !important' : 'black',
              textTransform: 'none',
              fontWeight: 400,
              fontSize: '12px',
              textDecoration: 'none !important',
              '&:hover': {
                textDecoration: 'none'
              },
              '@media(max-width: 600px)': {
                fontSize: '11px !important'
              }
            }}
            aria-label="View BAT status"
            aria-pressed={statusType === 'BAT'}
            onClick={() => {
              setStatus('BAT');
              navigateToStatus('BAT');
              announceStatus('BAT', label);
            }}
          >
            {data.toggle[2]}
          </Button>
        </Box>
      </fieldset>
    </Box>
  );
};

export default SupportStatusToggle;
